if (!customElements.get('burnt-peanut-dressing-room')) {
  customElements.define(
    'burnt-peanut-dressing-room',
    class BurntPeanutDressingRoom extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.scene = this.querySelector('[data-scene]');
        this.dropZone = this.querySelector('[data-drop-zone]');
        this.peanutShell = this.querySelector('.bpdr-peanut-shell');
        this.peanutImage = this.querySelector('.bpdr-peanut');
        this.wornGarment = this.querySelector('[data-worn-garment]');
        this.headwear = this.querySelector('[data-headwear]');
        this.sideAccessory = this.querySelector('[data-side-accessory]');
        this.talkingMouth = this.querySelector('[data-talking-mouth]');
        this.clearButton = this.querySelector('[data-clear]');
        this.faceToggle = this.querySelector('[data-toggle-face]');
        this.faceToggleLabel = this.querySelector('[data-toggle-face-label]');
        this.hintEl = this.querySelector('[data-hint]');
        this.statusEl = this.querySelector('[data-status]');
        this.buySign = this.querySelector('[data-buy-sign]');
        this.buySignTitle = this.querySelector('[data-buy-sign-title]');
        this.buySignPrice = this.querySelector('[data-buy-sign-price]');
        this.rackGrid = this.querySelector('.bpdr-rack-grid');
        this.rackScrollPrev = this.querySelector('[data-rack-scroll-prev]');
        this.rackScrollNext = this.querySelector('[data-rack-scroll-next]');
        this.garmentButtons = Array.from(this.querySelectorAll('[data-garment-id]'));
        this.defaultPeanutState = this.peanutImage
          ? {
              src: this.peanutImage.getAttribute('src') || '',
              alt: this.peanutImage.getAttribute('alt') || '',
              width: this.peanutImage.getAttribute('width') || '',
              height: this.peanutImage.getAttribute('height') || '',
              backSrc: this.peanutImage.dataset.defaultBackImage || '',
              backWidth: this.peanutImage.dataset.defaultBackWidth || this.peanutImage.getAttribute('width') || '',
              backHeight: this.peanutImage.dataset.defaultBackHeight || this.peanutImage.getAttribute('height') || '',
            }
          : null;

        this.skipClickUntil = 0;
        this.dragThreshold = 12;
        this.defaultHintText = this.hintEl?.textContent?.trim() || '';
        this.statusTimer = null;
        this.buySignTimer = null;
        this.turnSwapTimer = null;
        this.turnEndTimer = null;
        this.fallingAccessoryTimer = null;
        this.dragState = null;
        this.preloadedImages = [];
        this.soundPlayer = new Audio();
        this.soundPlayer.preload = 'auto';
        this.soundPlayToken = 0;
        this.audioPlaybackUnlocked = false;
        this.audioUnlockPromise = null;
        this.audioUnlockSource = '';
        this.activeAudio = this.soundPlayer;
        this.activeAudioSoundSrc = '';
        this.audioContext = null;
        this.audioAnalyser = null;
        this.audioAnalyserData = null;
        this.analysedAudio = null;
        this.audioIsAudible = true;
        this.talkingFrameTimer = null;
        this.talkingFrameIndex = 0;
        this.currentOutfitButton = null;
        this.currentAccessoryButton = null;
        this.currentSideAccessoryButton = null;
        this.currentFace = 'front';

        this.handlePointerMove = this.handlePointerMove.bind(this);
        this.handlePointerEnd = this.handlePointerEnd.bind(this);
        this.handlePointerCancel = this.handlePointerCancel.bind(this);
        this.handleTouchMove = this.handleTouchMove.bind(this);
        this.handleTouchEnd = this.handleTouchEnd.bind(this);
        this.handleTouchCancel = this.handleTouchCancel.bind(this);
        this.handleRackScroll = this.handleRackScroll.bind(this);
        this.handleRackResize = this.handleRackResize.bind(this);

        this.bindEvents();
        this.preloadImages();
        this.applyCurrentState();
        this.updateRackScrollIndicators();
        window.requestAnimationFrame(() => this.updateRackScrollIndicators());
      }

      disconnectedCallback() {
        this.teardownDrag();
        this.clearStatusTimer();
        this.clearBuySignTimer();
        this.clearFallingAccessory();
        this.cancelTurn();
        this.stopActiveAudio();
        if (this.rackGrid) {
          this.rackGrid.removeEventListener('scroll', this.handleRackScroll);
        }
        window.removeEventListener('resize', this.handleRackResize);
        this.preloadedImages = [];
      }

      bindEvents() {
        this.garmentButtons.forEach((button) => {
          button.draggable = false;
          const image = button.querySelector('img');
          if (image) image.draggable = false;

          button.addEventListener('pointerdown', (event) => this.handlePointerDown(event, button));
          button.addEventListener('touchstart', (event) => this.handleTouchStart(event, button), { passive: false });
          button.addEventListener('click', (event) => this.handleGarmentClick(event, button));
        });

        if (this.clearButton) {
          this.clearButton.addEventListener('click', () => this.clearGarment());
        }

        if (this.faceToggle) {
          this.faceToggle.addEventListener('click', () => this.handleFaceToggle());
        }

        if (this.rackGrid) {
          this.rackGrid.addEventListener('scroll', this.handleRackScroll, { passive: true });
        }

        if (this.rackScrollPrev) {
          this.rackScrollPrev.addEventListener('click', () => this.scrollRack(-1));
        }

        if (this.rackScrollNext) {
          this.rackScrollNext.addEventListener('click', () => this.scrollRack(1));
        }

        window.addEventListener('resize', this.handleRackResize);
      }

      handleRackScroll() {
        this.updateRackScrollIndicators();
      }

      handleRackResize() {
        this.updateRackScrollIndicators();
      }

      scrollRack(direction = 1) {
        if (!this.rackGrid) return;

        const distance = this.rackGrid.clientWidth * 0.72 * direction;
        this.rackGrid.scrollBy({
          left: distance,
          behavior: 'smooth',
        });
      }

      updateRackScrollIndicators() {
        if (!this.rackGrid || !this.rackScrollPrev || !this.rackScrollNext) return;

        const maxScroll = this.rackGrid.scrollWidth - this.rackGrid.clientWidth;
        const hasOverflow = maxScroll > 4;
        const atStart = this.rackGrid.scrollLeft <= 4;
        const atEnd = this.rackGrid.scrollLeft >= maxScroll - 4;

        this.rackScrollPrev.hidden = !hasOverflow || atStart;
        this.rackScrollNext.hidden = !hasOverflow || atEnd;
      }

      handleGarmentClick(event, button) {
        event.preventDefault();

        if (performance.now() < this.skipClickUntil) {
          return;
        }

        const label = button?.dataset.garmentLabel || 'item';
        this.showStatus(`Drag ${label} onto Peanut to try it on.`);
      }

      handlePointerDown(event, button) {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        if (event.pointerType === 'touch') return;

        this.beginDrag(button, event.clientX, event.clientY, { pointerId: event.pointerId });

        if (typeof button.setPointerCapture === 'function') {
          try {
            button.setPointerCapture(event.pointerId);
          } catch (error) {
            // Some mobile browsers throw if capture is unavailable for this pointer.
          }
        }

        window.addEventListener('pointermove', this.handlePointerMove);
        window.addEventListener('pointerup', this.handlePointerEnd);
        window.addEventListener('pointercancel', this.handlePointerCancel);

        event.preventDefault();
      }

      handleTouchStart(event, button) {
        if (event.touches.length !== 1 || this.dragState) return;

        const touch = event.changedTouches[0];
        this.unlockAudioPlayback(button);
        this.beginDrag(button, touch.clientX, touch.clientY, { touchId: touch.identifier });

        window.addEventListener('touchmove', this.handleTouchMove, { passive: false });
        window.addEventListener('touchend', this.handleTouchEnd, { passive: false });
        window.addEventListener('touchcancel', this.handleTouchCancel, { passive: false });
      }

      beginDrag(button, clientX, clientY, options = {}) {
        const image = button.querySelector('img');
        const imageRect = image ? image.getBoundingClientRect() : button.getBoundingClientRect();
        const ghost = document.createElement('img');
        const customGhostWidth = Number.parseFloat(button.dataset.dragWidth || '');
        const ghostWidth = Number.isFinite(customGhostWidth)
          ? customGhostWidth
          : Math.min(Math.max(imageRect.width * 1.04, 132), 260);
        const ghostSrc = button.dataset.dragImage || image?.currentSrc || button.dataset.garmentImage;

        ghost.className = 'bpdr-drag-ghost';
        ghost.src = ghostSrc;
        ghost.alt = '';
        ghost.draggable = false;
        ghost.style.width = `${ghostWidth}px`;
        document.body.appendChild(ghost);

        this.dragState = {
          button,
          pointerId: options.pointerId,
          touchId: options.touchId,
          ghost,
          startX: clientX,
          startY: clientY,
          moved: false,
        };

        button.classList.add('is-dragging');
        if (this.scene) {
          this.scene.classList.add('is-dragging');
        }

        this.positionGhost(clientX, clientY);
        this.updateDropTarget(clientX, clientY);
        this.showStatus('Drop it onto Peanut.');
      }

      handlePointerMove(event) {
        if (!this.dragState || event.pointerId !== this.dragState.pointerId) return;

        this.moveDrag(event.clientX, event.clientY, event);
      }

      handleTouchMove(event) {
        if (!this.dragState || this.dragState.touchId == null) return;

        const touch = this.getChangedTouch(event);
        if (!touch) return;

        this.moveDrag(touch.clientX, touch.clientY, event);
      }

      moveDrag(clientX, clientY, event) {
        if (!this.dragState) return;

        const deltaX = clientX - this.dragState.startX;
        const deltaY = clientY - this.dragState.startY;
        const distance = Math.hypot(deltaX, deltaY);
        const isTouchDrag = this.dragState.touchId != null;
        const isHorizontalSwipe = Math.abs(deltaX) > Math.abs(deltaY) * 1.25;

        if (isTouchDrag && distance > this.dragThreshold && isHorizontalSwipe) {
          this.teardownDrag();
          return;
        }

        if (distance > this.dragThreshold) {
          this.dragState.moved = true;
        }

        if (Math.abs(deltaY) > Math.abs(deltaX) * 0.85 && event.cancelable) {
          event.preventDefault();
        }

        this.positionGhost(clientX, clientY);
        const overDropZone = this.updateDropTarget(clientX, clientY);

        if (this.dragState.ghost) {
          this.dragState.ghost.classList.toggle('is-targeted', overDropZone);
        }
      }

      handlePointerEnd(event) {
        if (!this.dragState || event.pointerId !== this.dragState.pointerId) return;

        this.finishDrag(event.clientX, event.clientY);
      }

      handleTouchEnd(event) {
        if (!this.dragState || this.dragState.touchId == null) return;

        const touch = this.getChangedTouch(event);
        if (!touch) return;

        this.finishDrag(touch.clientX, touch.clientY);
        event.preventDefault();
      }

      finishDrag(clientX, clientY) {
        if (!this.dragState) return;

        const { button, moved } = this.dragState;
        const overDropZone = this.updateDropTarget(clientX, clientY);

        if (overDropZone && moved) {
          const soundStarted = this.playButtonSound(button);
          this.equipItem(button, { skipSound: soundStarted });
        } else if (moved) {
          this.showStatus('Drop it onto Peanut to try it on.');
        }

        this.skipClickUntil = performance.now() + 240;
        this.teardownDrag();
      }

      handlePointerCancel(event) {
        if (!this.dragState || event.pointerId !== this.dragState.pointerId) return;

        this.teardownDrag();
      }

      handleTouchCancel(event) {
        if (!this.dragState || this.dragState.touchId == null) return;

        const touch = this.getChangedTouch(event);
        if (!touch) return;

        this.teardownDrag();
      }

      getChangedTouch(event) {
        if (!this.dragState || this.dragState.touchId == null) return null;

        return Array.from(event.changedTouches).find((touch) => touch.identifier === this.dragState.touchId) || null;
      }

      handleFaceToggle() {
        if (!this.canShowBackView()) return;

        const nextFace = this.currentFace === 'back' ? 'front' : 'back';
        const label = this.currentOutfitButton?.dataset.garmentLabel || 'Peanut';

        this.turnPeanut(() => {
          this.currentFace = nextFace;
          this.applyCurrentState();
          this.showStatus(nextFace === 'back' ? `Showing the back of ${label}.` : `Showing the front of ${label}.`);
        });
      }

      equipItem(button, options = {}) {
        if (!button) return;

        if (button.dataset.sideAccessoryImage) {
          this.equipSideAccessory(button, options);
          return;
        }

        const itemType = button.dataset.itemType === 'accessory' || button.dataset.headwearImage ? 'accessory' : 'outfit';

        if (itemType === 'accessory') {
          this.equipAccessory(button, options);
          return;
        }

        this.equipOutfit(button, options);
      }

      equipOutfit(button, options = {}) {
        const label = button.dataset.garmentLabel || 'Garment';
        const shouldTurn = this.currentFace === 'back' && this.canShowBackView();

        this.clearFallingAccessory();

        const applySelection = () => {
          this.currentOutfitButton = button;
          this.currentFace = 'front';
          this.applyCurrentState();
          if (!options.skipSound) this.playButtonSound(button);
          this.showBuySign(button);
          this.showStatus(`${label} is on Peanut.`);
        };

        if (shouldTurn) {
          this.turnPeanut(applySelection);
          return;
        }

        applySelection();
      }

      equipAccessory(button, options = {}) {
        const label = button.dataset.garmentLabel || 'Accessory';

        this.clearFallingAccessory();
        this.currentAccessoryButton = button;
        this.applyCurrentState();
        if (!options.skipSound) this.playButtonSound(button);
        this.showBuySign(button);
        this.showStatus(`${label} is on Peanut.`);
      }

      equipSideAccessory(button, options = {}) {
        const label = button.dataset.garmentLabel || 'Accessory';

        const applySelection = () => {
          this.currentSideAccessoryButton = button;
          this.applyCurrentState();
          if (!options.skipSound) this.playButtonSound(button);
          this.showBuySign(button);
          this.showStatus(`${label} is on Peanut.`);
        };

        this.clearFallingAccessory();

        if (button.dataset.fallingImage) {
          this.hideSideAccessory();
          this.showFallingAccessory(button);
          this.showStatus(`${label} is falling onto Peanut.`);

          this.fallingAccessoryTimer = window.setTimeout(() => {
            this.fallingAccessoryTimer = null;
            this.clearFallingAccessory();
            applySelection();
          }, 560);
          return;
        }

        applySelection();
      }

      teardownDrag() {
        if (
          this.dragState?.button &&
          this.dragState.pointerId != null &&
          typeof this.dragState.button.releasePointerCapture === 'function' &&
          typeof this.dragState.button.hasPointerCapture === 'function' &&
          this.dragState.button.hasPointerCapture(this.dragState.pointerId)
        ) {
          try {
            this.dragState.button.releasePointerCapture(this.dragState.pointerId);
          } catch (error) {
            // Capture may already be released by the browser.
          }
        }

        if (this.dragState?.button) {
          this.dragState.button.classList.remove('is-dragging');
        }

        if (this.dragState?.ghost) {
          this.dragState.ghost.remove();
        }

        if (this.scene) {
          this.scene.classList.remove('is-dragging');
        }

        if (this.dropZone) {
          this.dropZone.classList.remove('is-targeted');
        }

        window.removeEventListener('pointermove', this.handlePointerMove);
        window.removeEventListener('pointerup', this.handlePointerEnd);
        window.removeEventListener('pointercancel', this.handlePointerCancel);
        window.removeEventListener('touchmove', this.handleTouchMove);
        window.removeEventListener('touchend', this.handleTouchEnd);
        window.removeEventListener('touchcancel', this.handleTouchCancel);

        this.dragState = null;
      }

      positionGhost(clientX, clientY) {
        if (!this.dragState?.ghost) return;

        this.dragState.ghost.style.left = `${clientX}px`;
        this.dragState.ghost.style.top = `${clientY}px`;
      }

      updateDropTarget(clientX, clientY) {
        if (!this.dropZone) return false;

        const rect = this.dropZone.getBoundingClientRect();
        const overDropZone =
          clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom;

        this.dropZone.classList.toggle('is-targeted', overDropZone);
        return overDropZone;
      }

      applyCurrentState() {
        const face = this.getRenderableFace();
        this.currentFace = face;

        this.applyOutfitState(face);
        this.applyAccessoryState(face);
        this.applySideAccessoryState(face);
        this.updateSelectedButtons();

        if (this.dropZone) {
          this.dropZone.classList.toggle('has-garment', this.hasAnySelection());
          this.dropZone.classList.toggle('is-facing-back', face === 'back');
        }

        if (this.clearButton) {
          this.clearButton.hidden = !this.hasAnySelection();
        }

        this.updateFaceToggle();
      }

      applyOutfitState(face) {
        const button = this.currentOutfitButton;

        if (!button) {
          this.hideWornGarment();
          this.resetPeanutImage(face);
          return;
        }

        const label = button.dataset.garmentLabel || 'Garment';

        if (button.dataset.characterImage && this.peanutImage) {
          const showingBack = face === 'back' && this.hasBackView(button);
          const image = showingBack ? button.dataset.characterBackImage : button.dataset.characterImage;
          const width = showingBack
            ? button.dataset.characterBackWidth || button.dataset.characterWidth || '2200'
            : button.dataset.characterWidth || '2200';
          const height = showingBack
            ? button.dataset.characterBackHeight || button.dataset.characterHeight || '2200'
            : button.dataset.characterHeight || '2200';
          const scale = showingBack
            ? button.dataset.characterBackScale || button.dataset.characterScale || '1.16'
            : button.dataset.characterScale || '1.16';

          this.hideWornGarment();
          this.peanutImage.setAttribute('src', image);
          this.peanutImage.setAttribute('alt', `${label} on Peanut${showingBack ? ', back view' : ''}`);
          this.peanutImage.setAttribute('width', width);
          this.peanutImage.setAttribute('height', height);
          this.peanutImage.style.setProperty('--bpdr-peanut-scale', scale);
          return;
        }

        if (!button.dataset.garmentImage || !this.wornGarment) {
          this.hideWornGarment();
          this.resetPeanutImage(face);
          return;
        }

        this.resetPeanutImage(face);
        this.wornGarment.src = button.dataset.garmentImage;
        this.wornGarment.alt = `${label} on Peanut`;
        this.wornGarment.hidden = false;
        this.wornGarment.style.setProperty('--bpdr-wear-width', `${button.dataset.wearWidth || 150}%`);
        this.wornGarment.style.setProperty('--bpdr-wear-top', `${button.dataset.wearTop || 58}%`);
        this.wornGarment.style.setProperty('--bpdr-wear-left', `${button.dataset.wearLeft || 50}%`);
      }

      applyAccessoryState(face) {
        const button = this.currentAccessoryButton;

        if (!button || !this.headwear || !button.dataset.headwearImage) {
          this.hideHeadwear();
          return;
        }

        const showingBack = face === 'back' && this.canShowBackView() && button.dataset.headwearBackImage;
        const image = showingBack ? button.dataset.headwearBackImage : button.dataset.headwearImage;
        const width = showingBack
          ? button.dataset.headwearBackWidth || button.dataset.headwearWidth || '120'
          : button.dataset.headwearWidth || '120';
        const top = showingBack
          ? button.dataset.headwearBackTop || button.dataset.headwearTop || '1'
          : button.dataset.headwearTop || '1';
        const left = showingBack
          ? button.dataset.headwearBackLeft || button.dataset.headwearLeft || '50'
          : button.dataset.headwearLeft || '50';
        const label = button.dataset.garmentLabel || 'Accessory';

        this.headwear.src = image;
        this.headwear.alt = `${label} on Peanut${showingBack ? ', back view' : ''}`;
        this.headwear.hidden = false;
        this.headwear.style.setProperty('--bpdr-headwear-width', `${width}%`);
        this.headwear.style.setProperty('--bpdr-headwear-top', `${top}%`);
        this.headwear.style.setProperty('--bpdr-headwear-left', `${left}%`);
      }

      applySideAccessoryState(face) {
        const button = this.currentSideAccessoryButton;

        if (!button || !this.sideAccessory || !button.dataset.sideAccessoryImage) {
          this.hideSideAccessory();
          return;
        }

        const showingBack = face === 'back' && this.canShowBackView() && button.dataset.sideAccessoryBackImage;
        const image = showingBack ? button.dataset.sideAccessoryBackImage : button.dataset.sideAccessoryImage;
        const width = showingBack
          ? button.dataset.sideAccessoryBackWidth || button.dataset.sideAccessoryWidth || '100'
          : button.dataset.sideAccessoryWidth || '100';
        const scale = showingBack
          ? button.dataset.sideAccessoryBackScale || button.dataset.sideAccessoryScale || '0.84'
          : button.dataset.sideAccessoryScale || '0.84';
        const top = showingBack
          ? button.dataset.sideAccessoryBackTop || button.dataset.sideAccessoryTop || '0'
          : button.dataset.sideAccessoryTop || '0';
        const label = button.dataset.garmentLabel || 'Accessory';

        this.sideAccessory.src = image;
        this.sideAccessory.alt = `${label} on Peanut${showingBack ? ', back view' : ''}`;
        this.sideAccessory.hidden = false;
        this.sideAccessory.style.setProperty('--bpdr-side-accessory-width', `${width}%`);
        this.sideAccessory.style.setProperty('--bpdr-side-accessory-scale', scale);
        this.sideAccessory.style.setProperty('--bpdr-side-accessory-top', this.formatCssLength(top, 'px'));
      }

      updateSelectedButtons() {
        this.garmentButtons.forEach((button) => {
          const isSelected =
            button === this.currentOutfitButton ||
            button === this.currentAccessoryButton ||
            button === this.currentSideAccessoryButton;
          button.classList.toggle('is-selected', isSelected);
        });
      }

      clearGarment() {
        if (!this.hasAnySelection()) return;

        this.cancelTurn();
        this.stopActiveAudio();
        this.hideWornGarment();
        this.hideHeadwear();
        this.hideSideAccessory();
        this.resetPeanutImage();

        this.currentOutfitButton = null;
        this.currentAccessoryButton = null;
        this.currentSideAccessoryButton = null;
        this.currentFace = 'front';
        this.hideBuySign();

        this.updateSelectedButtons();

        if (this.dropZone) {
          this.dropZone.classList.remove('has-garment', 'is-facing-back');
        }

        if (this.clearButton) {
          this.clearButton.hidden = true;
        }

        this.updateFaceToggle();
        this.showStatus('Outfit removed.');
      }

      turnPeanut(onSwap) {
        this.cancelTurn();

        if (!this.peanutShell) {
          onSwap?.();
          return;
        }

        void this.peanutShell.offsetWidth;
        this.peanutShell.classList.add('is-turning');

        this.turnSwapTimer = window.setTimeout(() => {
          onSwap?.();
        }, 165);

        this.turnEndTimer = window.setTimeout(() => {
          if (this.peanutShell) {
            this.peanutShell.classList.remove('is-turning');
          }
        }, 360);
      }

      cancelTurn() {
        if (this.turnSwapTimer) {
          window.clearTimeout(this.turnSwapTimer);
          this.turnSwapTimer = null;
        }

        if (this.turnEndTimer) {
          window.clearTimeout(this.turnEndTimer);
          this.turnEndTimer = null;
        }

        if (this.peanutShell) {
          this.peanutShell.classList.remove('is-turning');
        }
      }

      hasAnySelection() {
        return Boolean(this.currentOutfitButton || this.currentAccessoryButton || this.currentSideAccessoryButton);
      }

      hasBackView(button) {
        return Boolean(button?.dataset.characterBackImage);
      }

      hasDefaultBackView() {
        return Boolean(this.defaultPeanutState?.backSrc);
      }

      canShowBackView() {
        if (this.currentOutfitButton) {
          return this.hasBackView(this.currentOutfitButton);
        }

        return this.hasDefaultBackView();
      }

      getRenderableFace() {
        return this.currentFace === 'back' && this.canShowBackView() ? 'back' : 'front';
      }

      updateFaceToggle() {
        if (!this.faceToggle) return;

        const canShowBack = this.canShowBackView();
        const showingBack = this.getRenderableFace() === 'back';
        const subject = this.currentOutfitButton ? 'the garment' : 'Peanut';

        this.faceToggle.hidden = !canShowBack;
        this.faceToggle.setAttribute('aria-pressed', showingBack ? 'true' : 'false');
        this.faceToggle.setAttribute(
          'aria-label',
          showingBack ? `Show the front of ${subject}` : `Show the back of ${subject}`
        );

        if (this.faceToggleLabel) {
          this.faceToggleLabel.textContent = showingBack ? 'See front' : 'See back';
        }

        this.faceToggle.classList.toggle('is-back', showingBack);
      }

      resetPeanutImage(face = 'front') {
        if (!this.peanutImage || !this.defaultPeanutState) return;

        const showingBack = face === 'back' && this.hasDefaultBackView();

        this.peanutImage.setAttribute('src', showingBack ? this.defaultPeanutState.backSrc : this.defaultPeanutState.src);
        this.peanutImage.setAttribute(
          'alt',
          showingBack ? `${this.defaultPeanutState.alt}, back view` : this.defaultPeanutState.alt
        );
        this.peanutImage.setAttribute(
          'width',
          showingBack ? this.defaultPeanutState.backWidth : this.defaultPeanutState.width
        );
        this.peanutImage.setAttribute(
          'height',
          showingBack ? this.defaultPeanutState.backHeight : this.defaultPeanutState.height
        );
        this.peanutImage.style.removeProperty('--bpdr-peanut-scale');
      }

      hideWornGarment() {
        if (!this.wornGarment) return;

        this.wornGarment.hidden = true;
        this.wornGarment.removeAttribute('src');
        this.wornGarment.removeAttribute('alt');
        this.wornGarment.style.removeProperty('--bpdr-wear-width');
        this.wornGarment.style.removeProperty('--bpdr-wear-top');
        this.wornGarment.style.removeProperty('--bpdr-wear-left');
      }

      hideHeadwear() {
        if (!this.headwear) return;

        this.headwear.hidden = true;
        this.headwear.removeAttribute('src');
        this.headwear.removeAttribute('alt');
        this.headwear.style.removeProperty('--bpdr-headwear-width');
        this.headwear.style.removeProperty('--bpdr-headwear-top');
        this.headwear.style.removeProperty('--bpdr-headwear-left');
      }

      hideSideAccessory() {
        if (!this.sideAccessory) return;

        this.sideAccessory.hidden = true;
        this.sideAccessory.removeAttribute('src');
        this.sideAccessory.removeAttribute('alt');
        this.sideAccessory.style.removeProperty('--bpdr-side-accessory-width');
        this.sideAccessory.style.removeProperty('--bpdr-side-accessory-scale');
        this.sideAccessory.style.removeProperty('--bpdr-side-accessory-top');
      }

      formatCssLength(value, defaultUnit = 'px') {
        const cssValue = `${value || 0}`.trim();

        if (!cssValue || cssValue === '0') return '0';
        if (/^-?\d+(\.\d+)?$/.test(cssValue)) return `${cssValue}${defaultUnit}`;

        return cssValue;
      }

      showFallingAccessory(button) {
        if (!this.peanutShell || !button.dataset.fallingImage) return;

        const fallingAccessory = document.createElement('img');
        fallingAccessory.className = 'bpdr-falling-accessory';
        fallingAccessory.src = button.dataset.fallingImage;
        fallingAccessory.alt = '';
        fallingAccessory.draggable = false;
        fallingAccessory.style.setProperty('--bpdr-falling-accessory-width', `${button.dataset.fallingWidth || 30}%`);

        this.peanutShell.appendChild(fallingAccessory);

        window.requestAnimationFrame(() => {
          fallingAccessory.classList.add('is-falling');
        });
      }

      clearFallingAccessory() {
        if (this.fallingAccessoryTimer) {
          window.clearTimeout(this.fallingAccessoryTimer);
          this.fallingAccessoryTimer = null;
        }

        this.querySelectorAll('.bpdr-falling-accessory').forEach((fallingAccessory) => {
          fallingAccessory.remove();
        });
      }

      showBuySign(button) {
        if (!this.buySign || !button) return;

        if (button.dataset.hideBuySign === 'true') {
          this.hideBuySign();
          return;
        }

        const productUrl = button.dataset.productUrl || '#';
        const productTitle = button.dataset.productTitle || button.dataset.garmentLabel || 'Product';
        const productPrice = button.dataset.productPrice || '';
        const wasVisible = this.buySign.classList.contains('is-visible') && !this.buySign.hidden;

        this.clearBuySignTimer();

        if (wasVisible) {
          this.buySign.classList.add('is-switching');
          this.buySign.classList.remove('is-visible');
          this.buySignTimer = window.setTimeout(() => {
            this.updateBuySign(button, productUrl, productTitle, productPrice);
            this.dropBuySign();
          }, 210);
          return;
        }

        this.updateBuySign(button, productUrl, productTitle, productPrice);
        this.dropBuySign();
      }

      updateBuySign(button, productUrl, productTitle, productPrice) {
        if (!this.buySign) return;

        this.buySign.href = productUrl;
        this.buySign.dataset.productId = button.dataset.productId || '';
        this.buySign.setAttribute(
          'aria-label',
          productPrice ? `Buy ${productTitle} for ${productPrice}` : `Buy ${productTitle}`
        );

        if (this.buySignTitle) {
          this.buySignTitle.textContent = productTitle;
        }

        if (this.buySignPrice) {
          this.buySignPrice.textContent = productPrice;
        }
      }

      dropBuySign() {
        if (!this.buySign) return;

        this.clearBuySignTimer();
        this.buySign.hidden = false;
        this.buySign.classList.remove('is-visible');

        window.requestAnimationFrame(() => {
          if (!this.buySign) return;

          this.buySign.classList.remove('is-switching');
          this.buySign.classList.add('is-visible');
        });
      }

      hideBuySign() {
        if (!this.buySign) return;

        this.clearBuySignTimer();
        this.buySign.classList.add('is-switching');
        this.buySign.classList.remove('is-visible');

        this.buySignTimer = window.setTimeout(() => {
          if (!this.buySign) return;

          this.buySign.hidden = true;
          this.buySign.classList.remove('is-switching');
        }, 220);
      }

      clearBuySignTimer() {
        if (!this.buySignTimer) return;

        window.clearTimeout(this.buySignTimer);
        this.buySignTimer = null;
      }

      preloadImages() {
        const sources = new Set();

        if (this.defaultPeanutState?.src) sources.add(this.defaultPeanutState.src);
        if (this.defaultPeanutState?.backSrc) sources.add(this.defaultPeanutState.backSrc);

        this.garmentButtons.forEach((button) => {
          [
            button.dataset.characterImage,
            button.dataset.characterBackImage,
            button.dataset.garmentImage,
            button.dataset.headwearImage,
            button.dataset.headwearBackImage,
            button.dataset.sideAccessoryImage,
            button.dataset.sideAccessoryBackImage,
            button.dataset.dragImage,
            button.dataset.fallingImage,
          ].forEach((src) => {
            if (src) sources.add(src);
          });
        });

        this.getMouthFrames().forEach((src) => {
          if (src) sources.add(src);
        });

        sources.forEach((src) => {
          const image = new Image();
          image.decoding = 'async';
          image.src = src;
          this.preloadedImages.push(image);
        });
      }

      playButtonSound(button) {
        const soundSrc = button?.dataset.sound;
        if (!soundSrc) return false;

        const audio = this.soundPlayer;
        if (!audio) return false;

        if (this.audioUnlockPromise && this.audioUnlockSource === soundSrc) {
          const unlockPromise = this.audioUnlockPromise;

          unlockPromise.then(() => {
            if (this.audioUnlockPromise) return;

            this.playButtonSound(button);
          });

          return true;
        }

        this.audioUnlockPromise = null;
        this.audioUnlockSource = '';

        const token = this.soundPlayToken + 1;
        this.soundPlayToken = token;
        this.stopTalkingAnimation();
        audio.onended = null;
        audio.pause();
        this.resetAudioPosition(audio);
        audio.removeAttribute('src');
        audio.load();

        this.activeAudio = audio;
        this.activeAudioSoundSrc = soundSrc;
        audio.src = soundSrc;
        audio.preload = 'auto';
        audio.loop = false;
        audio.muted = false;
        audio.volume = 1;
        audio.load();
        this.resetAudioPosition(audio);
        audio.onended = () => {
          if (this.soundPlayToken === token) this.stopTalkingAnimation();
        };

        let playPromise;

        try {
          playPromise = audio.play();
        } catch (error) {
          if (this.soundPlayToken === token) this.stopTalkingAnimation();
          return false;
        }

        if (playPromise && typeof playPromise.then === 'function') {
          playPromise
            .then(() => {
              if (this.soundPlayToken !== token || this.activeAudio !== audio) return;

              this.setupAudioAnalyser(audio);
              this.resumeAudioAnalyser();
              this.startTalkingAnimation();
            })
            .catch(() => {
              if (this.soundPlayToken === token) this.stopTalkingAnimation();
            });
        } else {
          this.setupAudioAnalyser(audio);
          this.resumeAudioAnalyser();
          this.startTalkingAnimation();
        }

        if (playPromise && typeof playPromise.catch === 'function' && typeof playPromise.then !== 'function') {
          playPromise.catch(() => {
            if (this.soundPlayToken === token) this.stopTalkingAnimation();
          });
        }

        return true;
      }

      unlockAudioPlayback(button) {
        const soundSrc = button?.dataset.sound;
        if (!soundSrc || this.audioPlaybackUnlocked || this.audioUnlockPromise) return;

        const audio = this.soundPlayer;
        if (!audio) return;

        this.soundPlayToken += 1;
        this.stopTalkingAnimation();
        audio.onended = null;
        audio.pause();
        this.resetAudioPosition(audio);
        audio.removeAttribute('src');
        audio.load();

        this.activeAudio = audio;
        this.activeAudioSoundSrc = soundSrc;
        this.audioUnlockSource = soundSrc;
        audio.src = soundSrc;
        audio.preload = 'auto';
        audio.loop = false;
        audio.muted = true;
        audio.volume = 0;
        audio.load();
        this.resetAudioPosition(audio);

        let playPromise;

        try {
          playPromise = audio.play();
        } catch (error) {
          this.audioUnlockSource = '';
          audio.muted = false;
          audio.volume = 1;
          return;
        }

        let unlockPromise;
        unlockPromise = Promise.resolve(playPromise)
          .then(() => {
            if (this.audioUnlockPromise !== unlockPromise) return;

            this.audioPlaybackUnlocked = true;
            audio.pause();
            this.resetAudioPosition(audio);
            audio.muted = false;
            audio.volume = 1;
            this.audioUnlockPromise = null;
          })
          .catch(() => {
            if (this.audioUnlockPromise !== unlockPromise) return;

            audio.pause();
            this.resetAudioPosition(audio);
            audio.muted = false;
            audio.volume = 1;
            this.audioUnlockPromise = null;
            this.audioUnlockSource = '';
          });

        this.audioUnlockPromise = unlockPromise;
      }

      stopActiveAudio() {
        this.soundPlayToken += 1;
        this.stopTalkingAnimation();
        this.audioUnlockPromise = null;
        this.audioUnlockSource = '';

        if (!this.activeAudio) return;

        this.activeAudio.onended = null;
        this.activeAudio.pause();
        this.resetAudioPosition(this.activeAudio);
        this.activeAudioSoundSrc = '';
      }

      resetAudioPosition(audio) {
        if (!audio) return;

        try {
          audio.currentTime = 0;
        } catch (error) {
          // Safari can reject currentTime changes while a new source is still loading.
        }
      }

      setupAudioAnalyser(audio) {
        const AudioContextClass = window.AudioContext || window.webkitAudioContext;
        if (!AudioContextClass || !audio) return;

        try {
          if (!this.audioContext) {
            this.audioContext = new AudioContextClass();
          }

          if (this.analysedAudio !== audio) {
            const source = this.audioContext.createMediaElementSource(audio);
            this.audioAnalyser = this.audioContext.createAnalyser();
            this.audioAnalyser.fftSize = 256;
            this.audioAnalyser.smoothingTimeConstant = 0.45;
            source.connect(this.audioAnalyser);
            this.audioAnalyser.connect(this.audioContext.destination);
            this.audioAnalyserData = new Uint8Array(this.audioAnalyser.fftSize);
            this.analysedAudio = audio;
          }
        } catch (error) {
          this.audioAnalyser = null;
          this.audioAnalyserData = null;
        }
      }

      resumeAudioAnalyser() {
        if (this.audioContext?.state === 'suspended') {
          this.audioContext.resume().catch(() => {});
        }
      }

      getAudioLevel() {
        if (!this.audioAnalyser || !this.audioAnalyserData) return 1;

        this.audioAnalyser.getByteTimeDomainData(this.audioAnalyserData);

        let sum = 0;
        for (const value of this.audioAnalyserData) {
          const centered = (value - 128) / 128;
          sum += centered * centered;
        }

        return Math.sqrt(sum / this.audioAnalyserData.length);
      }

      setClosedMouthFrame() {
        if (!this.talkingMouth) return;

        this.talkingMouth.hidden = false;
        this.talkingMouth.style.removeProperty('--bpdr-mouth-scale-x');
        this.talkingMouth.style.removeProperty('--bpdr-mouth-scale-y');
        this.talkingMouth.style.removeProperty('--bpdr-mouth-shift-x');
        this.talkingMouth.style.removeProperty('--bpdr-mouth-shift-y');

        if (this.talkingMouth.dataset.mouthClosed) {
          this.talkingMouth.src = this.talkingMouth.dataset.mouthClosed;
        }
      }

      getMouthFrames() {
        if (!this.talkingMouth) return [];

        return [
          this.talkingMouth.dataset.mouthClosed,
          this.talkingMouth.dataset.mouthSmall,
          this.talkingMouth.dataset.mouthMedium,
          this.talkingMouth.dataset.mouthWide,
        ].filter(Boolean);
      }

      getTalkingSequence() {
        if (!this.talkingMouth) return [];

        const closed = this.talkingMouth.dataset.mouthClosed;
        const small = this.talkingMouth.dataset.mouthSmall;
        const medium = this.talkingMouth.dataset.mouthMedium;
        const wide = this.talkingMouth.dataset.mouthWide;

        if (!closed || !small || !medium || !wide) {
          return this.getMouthFrames().map((src) => ({ src, delay: 64 }));
        }

        return [
          { src: closed, delay: 38, scaleX: 0.96, scaleY: 0.9, shiftY: '2px' },
          { src: small, delay: 48, scaleX: 0.98, scaleY: 0.94, shiftY: '1px' },
          { src: medium, delay: 56, scaleX: 1.02, scaleY: 1, shiftY: '0px' },
          { src: small, delay: 44, scaleX: 1, scaleY: 0.96, shiftY: '1px' },
          { src: wide, delay: 64, scaleX: 1.03, scaleY: 1.04, shiftY: '-1px' },
          { src: medium, delay: 50, scaleX: 0.99, scaleY: 1.01, shiftY: '0px' },
          { src: small, delay: 46, scaleX: 1.01, scaleY: 0.95, shiftY: '1px' },
          { src: closed, delay: 34, scaleX: 0.97, scaleY: 0.9, shiftY: '2px' },
          { src: small, delay: 42, scaleX: 0.99, scaleY: 0.98, shiftY: '0px' },
          { src: medium, delay: 58, scaleX: 1.01, scaleY: 1.02, shiftY: '-1px' },
          { src: wide, delay: 52, scaleX: 1, scaleY: 1.05, shiftY: '-2px' },
          { src: medium, delay: 48, scaleX: 1.02, scaleY: 1, shiftY: '0px' },
          { src: small, delay: 50, scaleX: 0.98, scaleY: 0.96, shiftY: '1px' },
        ];
      }

      startTalkingAnimation() {
        if (!this.talkingMouth) return;

        const sequence = this.getTalkingSequence();
        if (!sequence.length) return;

        this.stopTalkingAnimation();
        this.talkingMouth.hidden = false;

        const animate = () => {
          if (!this.activeAudio || this.activeAudio.paused || this.activeAudio.ended) {
            this.stopTalkingAnimation();
            return;
          }

          const audioLevel = this.getAudioLevel();
          const isAudible = audioLevel > 0.025;
          if (!isAudible) {
            this.audioIsAudible = false;
            this.setClosedMouthFrame();
            this.talkingFrameTimer = window.setTimeout(animate, 36);
            return;
          }

          if (!this.audioIsAudible) {
            this.talkingFrameIndex = 1;
          }
          this.audioIsAudible = true;

          const frame = sequence[this.talkingFrameIndex % sequence.length];
          this.talkingMouth.src = frame.src;
          this.talkingMouth.style.setProperty('--bpdr-mouth-scale-x', String(frame.scaleX || 1));
          this.talkingMouth.style.setProperty('--bpdr-mouth-scale-y', String(frame.scaleY || 1));
          this.talkingMouth.style.setProperty('--bpdr-mouth-shift-x', frame.shiftX || '0px');
          this.talkingMouth.style.setProperty('--bpdr-mouth-shift-y', frame.shiftY || '0px');
          this.talkingFrameIndex += 1;
          this.talkingFrameTimer = window.setTimeout(animate, frame.delay * 1.2 + Math.random() * 22);
        };

        this.talkingFrameTimer = window.setTimeout(animate, 0);
      }

      stopTalkingAnimation() {
        if (this.talkingFrameTimer) {
          window.clearTimeout(this.talkingFrameTimer);
          this.talkingFrameTimer = null;
        }

        this.talkingFrameIndex = 0;
        this.audioIsAudible = true;

        if (this.talkingMouth) {
          this.setClosedMouthFrame();
        }
      }

      clearStatusTimer() {
        if (this.statusTimer) {
          window.clearTimeout(this.statusTimer);
          this.statusTimer = null;
        }
      }

      showStatus(message) {
        if (!this.statusEl && !this.hintEl) return;

        if (this.hintEl) {
          this.hintEl.textContent = message;
        }

        if (this.statusEl) {
          this.statusEl.textContent = message;
        }

        this.clearStatusTimer();

        this.statusTimer = window.setTimeout(() => {
          if (this.hintEl && this.defaultHintText) {
            this.hintEl.textContent = this.defaultHintText;
          }

          if (this.statusEl) {
            this.statusEl.textContent = '';
          }
        }, 2600);
      }
    }
  );
}
